import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexto/AuthContexto';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Wallet, DollarSign, CreditCard, Banknote, ArrowRightLeft, Lock, Unlock, CheckCircle, AlertTriangle, FileText, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { hoyLocal } from '@/lib/fechas';

export default function CarwashCaja() {
  const { usuario } = useAuth();
  const puedeReabrir = ['propietario', 'administrativo'].includes(usuario?.rol);
  const [turnos, setTurnos] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [gastosDia, setGastosDia] = useState([]);
  const [fecha, setFecha] = useState(hoyLocal());
  const [cargando, setCargando] = useState(true);
  const [corteEstado, setCorteEstado] = useState({ cerrado: false, corte: null });
  const [cortesHistorial, setCortesHistorial] = useState([]);

  const [corteDialog, setCorteDialog] = useState(false);
  const [efectivoReal, setEfectivoReal] = useState('');
  const [notasCorte, setNotasCorte] = useState('');
  const [guardandoCorte, setGuardandoCorte] = useState(false);

  const [detalleDialog, setDetalleDialog] = useState(null);
  const [tab, setTab] = useState('dia'); // 'dia' | 'historial'

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde: fecha, hasta: fecha };
      const [tRes, cRes, vRes, gRes] = await Promise.all([
        api.get(`/carwash/turnos?fecha=${fecha}`),
        api.get(`/carwash/cortes-caja/verificar?fecha=${fecha}`),
        api.get('/carwash/ventas', { params: { fecha } }).catch(() => ({ data: [] })),
        api.get('/carwash/gastos', { params }).catch(() => ({ data: [] })),
      ]);
      setTurnos(tRes.data);
      setCorteEstado(cRes.data);
      setVentas(vRes.data);
      setGastosDia(gRes.data);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, [fecha]);

  const cargarHistorial = useCallback(async () => {
    try {
      const res = await api.get('/carwash/cortes-caja');
      setCortesHistorial(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (tab === 'historial') cargarHistorial(); }, [tab, cargarHistorial]);

  const cobrados = turnos.filter(t => t.pagado && t.estado !== 'cancelado');
  const sinCobrar = turnos.filter(t => !t.pagado && t.estado !== 'cancelado');
  const cancelados = turnos.filter(t => t.estado === 'cancelado');

  const totalLavados = cobrados.reduce((s, t) => s + (t.precio_final ?? t.precio), 0);
  const totalVentas = ventas.reduce((s, v) => s + (v.total || 0), 0);
  const totalGastos = gastosDia.reduce((s, g) => s + (g.monto || 0), 0);
  const totalIngresos = totalLavados + totalVentas;
  const totalPendiente = sinCobrar.reduce((s, t) => s + t.precio, 0);

  const porMetodo = { efectivo: 0, tarjeta: 0, transferencia: 0 };
  cobrados.forEach(t => { porMetodo[t.metodo_pago || 'efectivo'] += (t.precio_final ?? t.precio); });
  ventas.forEach(v => { porMetodo[v.metodo_pago || 'efectivo'] += (v.total || 0); });

  const porServicio = {};
  cobrados.forEach(t => {
    const sn = t.servicio_nombre || 'Otro';
    if (!porServicio[sn]) porServicio[sn] = { count: 0, total: 0 };
    porServicio[sn].count++;
    porServicio[sn].total += (t.precio_final ?? t.precio);
  });

  const abrirCorte = () => {
    setEfectivoReal(String(porMetodo.efectivo.toFixed(2)));
    setNotasCorte('');
    setCorteDialog(true);
  };

  const realizarCorte = async () => {
    setGuardandoCorte(true);
    try {
      const res = await api.post('/carwash/cortes-caja', {
        fecha,
        efectivo_real: parseFloat(efectivoReal) || 0,
        notas: notasCorte,
      });
      toast.success('Corte de caja realizado correctamente');
      setCorteDialog(false);
      setCorteEstado({ cerrado: true, corte: res.data });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al realizar corte');
    } finally { setGuardandoCorte(false); }
  };

  const descargarPDF = async (corteId) => {
    try {
      const res = await api.get(`/carwash/cortes-caja/${corteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Corte_${fecha}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch { toast.error('Error al generar PDF'); }
  };

  const reabrirCorte = async () => {
    if (!corteEstado.corte) return;
    if (!window.confirm(`Reabrir el corte de caja del ${fecha}? Esto permitira registrar nuevos turnos y movimientos.`)) return;
    try {
      await api.delete(`/carwash/cortes-caja/${corteEstado.corte.id}`);
      toast.success('Corte reabierto');
      setCorteEstado({ cerrado: false, corte: null });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reabrir');
    }
  };

  const diaCerrado = corteEstado.cerrado;
  const corteData = corteEstado.corte;
  const diferencia = parseFloat(efectivoReal || 0) - porMetodo.efectivo;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-caja-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Wallet className="w-8 h-8 text-cyan-400" /> Caja del Dia
        </h1>
        <div className="flex items-center gap-2">
          {tab === 'dia' && !diaCerrado && (
            <Button onClick={abrirCorte} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
              data-testid="realizar-corte-btn">
              <Lock className="w-4 h-4 mr-2" /> Realizar Corte de Caja
            </Button>
          )}
          {tab === 'dia' && diaCerrado && corteData && (
            <Button onClick={() => descargarPDF(corteData.id)} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="descargar-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Descargar PDF
            </Button>
          )}
          <div className="flex gap-1 bg-slate-900/60 p-1 rounded-sm">
            <button onClick={() => setTab('dia')} className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${tab === 'dia' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid="tab-dia">Dia Actual</button>
            <button onClick={() => setTab('historial')} className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${tab === 'historial' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid="tab-historial">Historial de Cortes</button>
          </div>
        </div>
      </div>

      {/* ═══ TAB: DIA ACTUAL ═══ */}
      {tab === 'dia' && (
        <>
          <div className="flex items-center gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Fecha</Label>
              <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-200 w-44" data-testid="caja-fecha-input" />
            </div>
            {diaCerrado && (
              <Badge className="bg-red-500/20 text-red-400 border-red-800 h-8 mt-5 px-3 flex items-center gap-1.5" data-testid="dia-cerrado-badge">
                <Lock className="w-3.5 h-3.5" /> Dia Cerrado
              </Badge>
            )}
            {diaCerrado && puedeReabrir && (
              <Button onClick={reabrirCorte} size="sm" variant="outline"
                className="mt-5 border-amber-700 text-amber-400 hover:bg-amber-500/10"
                data-testid="reabrir-corte-btn">
                <Unlock className="w-3.5 h-3.5 mr-1.5" /> Reabrir Caja
              </Button>
            )}
          </div>

          {/* Show frozen corte summary if day is closed */}
          {diaCerrado && corteData ? (
            <CorteResumen corte={corteData} onDescargar={descargarPDF} />
          ) : (
            <>
              {/* Resumen en vivo */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Ingresos Totales</p>
                  <p className="text-2xl font-bold text-emerald-400 font-mono">{formatoMXN(totalIngresos)}</p>
                  <p className="text-xs text-slate-500 mt-1">Lavados + Ventas</p>
                </CardContent></Card>
                <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Gastos</p>
                  <p className="text-2xl font-bold text-red-400 font-mono">{formatoMXN(totalGastos)}</p>
                  <p className="text-xs text-slate-500 mt-1">{gastosDia.length} registro(s)</p>
                </CardContent></Card>
                <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Ganancia Neta</p>
                  <p className={`text-2xl font-bold font-mono ${totalIngresos - totalGastos >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(totalIngresos - totalGastos)}</p>
                </CardContent></Card>
                <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pendiente</p>
                  <p className="text-2xl font-bold text-amber-400 font-mono">{formatoMXN(totalPendiente)}</p>
                  <p className="text-xs text-slate-500 mt-1">{sinCobrar.length} turno(s)</p>
                </CardContent></Card>
                <Card className="bg-slate-900/40 border-slate-800"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Turnos / Ventas</p>
                  <p className="text-2xl font-bold text-slate-100 font-mono">{cobrados.length} / {ventas.length}</p>
                  <p className="text-xs text-slate-500 mt-1">{cancelados.length} cancelado(s)</p>
                </CardContent></Card>
              </div>

              {/* Desglose por metodo */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-slate-900/40 border-slate-800">
                  <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Por Metodo de Pago</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {[
                      { key: 'efectivo', label: 'Efectivo', icon: Banknote, color: 'text-emerald-400' },
                      { key: 'tarjeta', label: 'Tarjeta', icon: CreditCard, color: 'text-blue-400' },
                      { key: 'transferencia', label: 'Transferencia', icon: ArrowRightLeft, color: 'text-violet-400' },
                    ].map(m => (
                      <div key={m.key} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-sm">
                        <div className="flex items-center gap-2">
                          <m.icon className={`w-4 h-4 ${m.color}`} />
                          <span className="text-sm text-slate-300">{m.label}</span>
                        </div>
                        <span className={`text-sm font-mono font-bold ${m.color}`}>{formatoMXN(porMetodo[m.key])}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/40 border-slate-800">
                  <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Por Servicio</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {Object.keys(porServicio).length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Sin cobros registrados</p>
                    ) : Object.entries(porServicio).sort((a, b) => b[1].total - a[1].total).map(([nombre, info]) => (
                      <div key={nombre} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-sm">
                        <div>
                          <p className="text-sm text-slate-300">{nombre}</p>
                          <p className="text-xs text-slate-500">{info.count} lavado(s)</p>
                        </div>
                        <span className="text-sm text-cyan-400 font-mono font-bold">{formatoMXN(info.total)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Detalle cobros */}
              {cobrados.length > 0 && (
                <Card className="bg-slate-900/40 border-slate-800">
                  <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Cobros de Lavados ({cobrados.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {cobrados.map(t => (
                        <div key={t.id} className="flex items-center justify-between p-2.5 bg-slate-800/30 rounded-sm">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono text-slate-300 font-bold w-24">{t.placa}</span>
                            <span className="text-xs text-slate-500">{t.servicio_nombre}</span>
                            <Badge className="text-[10px] bg-slate-700/50 text-slate-400 border-slate-600">{t.metodo_pago}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            {t.descuento_porcentaje > 0 && <Badge className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-800">-{t.descuento_porcentaje}%</Badge>}
                            <span className="text-sm text-emerald-400 font-mono font-bold">{formatoMXN(t.precio_final ?? t.precio)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detalle ventas mostrador */}
              {ventas.length > 0 && (
                <Card className="bg-slate-900/40 border-slate-800">
                  <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Ventas de Mostrador ({ventas.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {ventas.map(v => (
                        <div key={v.id} className="flex items-center justify-between p-2.5 bg-slate-800/30 rounded-sm">
                          <div className="flex items-center gap-3">
                            <Badge className="text-[10px] bg-violet-500/20 text-violet-400 border-violet-800 font-mono">{v.numero_venta}</Badge>
                            <span className="text-xs text-slate-500">{(v.items || []).length} producto(s)</span>
                            <Badge className="text-[10px] bg-slate-700/50 text-slate-400 border-slate-600">{v.metodo_pago || 'efectivo'}</Badge>
                          </div>
                          <span className="text-sm text-emerald-400 font-mono font-bold">{formatoMXN(v.total)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detalle gastos */}
              {gastosDia.length > 0 && (
                <Card className="bg-slate-900/40 border-slate-800">
                  <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-red-400">Gastos del Dia ({gastosDia.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {gastosDia.map(g => (
                        <div key={g.id} className="flex items-center justify-between p-2.5 bg-slate-800/30 rounded-sm">
                          <div className="flex items-center gap-3">
                            <Badge className="text-[10px] bg-slate-700/50 text-slate-400 border-slate-600 capitalize">{g.categoria}</Badge>
                            <span className="text-xs text-slate-400">{g.descripcion}</span>
                          </div>
                          <span className="text-sm text-red-400 font-mono font-bold">{formatoMXN(g.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* ═══ TAB: HISTORIAL DE CORTES ═══ */}
      {tab === 'historial' && (
        <div className="space-y-4">
          <h2 className="font-barlow text-lg font-bold text-slate-300 uppercase tracking-wider">Cortes de Caja Registrados</h2>
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cerrado Por</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Ingresos</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Gastos</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Neto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Arqueo</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cortesHistorial.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No hay cortes registrados</TableCell></TableRow>
                  ) : cortesHistorial.map(c => (
                    <TableRow key={c.id} className="border-slate-800/50 hover:bg-slate-800/40 cursor-pointer" onClick={() => setDetalleDialog(c)} data-testid={`corte-row-${c.id}`}>
                      <TableCell className="text-sm text-slate-300 font-medium">{c.fecha}</TableCell>
                      <TableCell className="text-sm text-slate-400">{c.cerrado_por}</TableCell>
                      <TableCell className="text-right text-sm font-bold text-green-400 font-mono">{formatoMXN(c.ingresos_total)}</TableCell>
                      <TableCell className="text-right text-sm font-bold text-red-400 font-mono">{formatoMXN(c.total_gastos)}</TableCell>
                      <TableCell className={`text-right text-sm font-bold font-mono ${c.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(c.ganancia_neta)}</TableCell>
                      <TableCell className="text-center">
                        {c.diferencia_arqueo === 0 ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-800 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />Cuadra</Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-800 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />{formatoMXN(c.diferencia_arqueo)}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setDetalleDialog(c); }} data-testid={`ver-corte-${c.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-green-400 hover:text-green-300" onClick={(e) => { e.stopPropagation(); descargarPDF(c.id); }} data-testid={`descargar-corte-${c.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ DIALOG: Realizar Corte ═══ */}
      <Dialog open={corteDialog} onOpenChange={setCorteDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="corte-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100 flex items-center gap-2">
              <Lock className="w-5 h-5 text-cyan-400" /> Corte de Caja - {fecha}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Al realizar el corte, los movimientos de este dia quedaran congelados y no se podran modificar.</p>

            {/* Resumen del dia */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-800/50 p-3 rounded-sm">
                <p className="text-[10px] uppercase text-slate-500">Ingresos Lavados</p>
                <p className="text-lg font-bold text-green-400 font-mono">{formatoMXN(totalLavados)}</p>
                <p className="text-[10px] text-slate-600">{cobrados.length} turno(s)</p>
              </div>
              <div className="bg-slate-800/50 p-3 rounded-sm">
                <p className="text-[10px] uppercase text-slate-500">Ventas Mostrador</p>
                <p className="text-lg font-bold text-green-400 font-mono">{formatoMXN(totalVentas)}</p>
                <p className="text-[10px] text-slate-600">{ventas.length} venta(s)</p>
              </div>
              <div className="bg-slate-800/50 p-3 rounded-sm">
                <p className="text-[10px] uppercase text-slate-500">Gastos del Dia</p>
                <p className="text-lg font-bold text-red-400 font-mono">{formatoMXN(totalGastos)}</p>
                <p className="text-[10px] text-slate-600">{gastosDia.length} registro(s)</p>
              </div>
              <div className="bg-slate-800/50 p-3 rounded-sm">
                <p className="text-[10px] uppercase text-slate-500">Cancelados</p>
                <p className="text-lg font-bold text-red-400 font-mono">{cancelados.length}</p>
              </div>
            </div>

            <div className="bg-slate-800/50 p-3 rounded-sm text-center">
              <p className="text-[10px] uppercase text-slate-500">Ganancia Neta del Dia</p>
              <p className={`text-xl font-bold font-mono ${totalIngresos - totalGastos >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(totalIngresos - totalGastos)}</p>
            </div>

            {/* Desglose metodos */}
            <div className="bg-slate-800/30 p-3 rounded-sm space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Desglose por Metodo</p>
              {['efectivo', 'tarjeta', 'transferencia'].map(m => porMetodo[m] > 0 && (
                <div key={m} className="flex justify-between text-sm">
                  <span className="text-slate-400 capitalize">{m}</span>
                  <span className="text-slate-200 font-mono">{formatoMXN(porMetodo[m])}</span>
                </div>
              ))}
            </div>

            {/* Arqueo de efectivo */}
            <div className="border border-cyan-800/50 bg-cyan-500/5 p-3 rounded-sm space-y-2">
              <p className="text-xs font-bold text-cyan-400 uppercase tracking-wider">Arqueo de Efectivo</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Efectivo esperado:</span>
                <span className="text-slate-200 font-mono font-bold">{formatoMXN(porMetodo.efectivo)}</span>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Efectivo contado en caja:</Label>
                <Input type="number" step="0.01" value={efectivoReal}
                  onChange={e => setEfectivoReal(e.target.value)}
                  placeholder="0.00"
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="efectivo-real-input" />
              </div>
              {efectivoReal && (
                <div className={`flex justify-between text-sm p-2 rounded-sm ${diferencia === 0 ? 'bg-green-500/10' : diferencia > 0 ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
                  <span className="text-slate-400">Diferencia:</span>
                  <span className={`font-mono font-bold ${diferencia === 0 ? 'text-green-400' : diferencia > 0 ? 'text-blue-400' : 'text-red-400'}`}>
                    {diferencia === 0 ? 'Cuadra' : `${diferencia > 0 ? '+' : ''}${formatoMXN(diferencia)}`}
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Notas del Corte</Label>
              <Input value={notasCorte} onChange={e => setNotasCorte(e.target.value)}
                placeholder="Observaciones del dia..."
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="notas-corte-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCorteDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={realizarCorte} disabled={guardandoCorte} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="confirmar-corte-btn">
              <Lock className="w-4 h-4 mr-2" /> {guardandoCorte ? 'Cerrando...' : 'Cerrar Dia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG: Detalle de Corte ═══ */}
      <Dialog open={!!detalleDialog} onOpenChange={() => setDetalleDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="detalle-corte-dialog">
          {detalleDialog && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => descargarPDF(detalleDialog.id)} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm text-xs" data-testid="descargar-pdf-detalle-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Descargar PDF
                </Button>
              </div>
              <CorteResumen corte={detalleDialog} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorteResumen({ corte }) {
  return (
    <div className="space-y-4" data-testid="corte-resumen">
      <div className="flex items-center justify-between">
        <h2 className="font-barlow text-xl font-bold text-slate-100 flex items-center gap-2">
          <FileText className="w-5 h-5 text-cyan-400" /> Reporte - {corte.fecha}
        </h2>
        <Badge className="bg-red-500/20 text-red-400 border-red-800 px-3"><Lock className="w-3 h-3 mr-1" />Cerrado</Badge>
      </div>
      <p className="text-xs text-slate-500">Cerrado por: {corte.cerrado_por}</p>

      {/* Metricas principales */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-800/50 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500">Ingresos</p>
          <p className="text-xl font-bold text-green-400 font-mono">{formatoMXN(corte.ingresos_total)}</p>
        </div>
        <div className="bg-slate-800/50 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500">Gastos</p>
          <p className="text-xl font-bold text-red-400 font-mono">{formatoMXN(corte.total_gastos)}</p>
        </div>
        <div className="bg-slate-800/50 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500">Ganancia Neta</p>
          <p className={`text-xl font-bold font-mono ${corte.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(corte.ganancia_neta)}</p>
        </div>
        <div className="bg-slate-800/50 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500">Turnos</p>
          <p className="text-xl font-bold text-slate-100 font-mono">{corte.total_turnos}</p>
          <p className="text-[10px] text-slate-600">{corte.turnos_cobrados} cobrados, {corte.turnos_cancelados} cancel.</p>
        </div>
      </div>

      {/* Desglose lavados + ventas */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-800/30 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500 mb-1">Lavados</p>
          <p className="text-sm font-bold text-green-400 font-mono">{formatoMXN(corte.ingresos_lavados)}</p>
        </div>
        <div className="bg-slate-800/30 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-slate-500 mb-1">Ventas Mostrador</p>
          <p className="text-sm font-bold text-green-400 font-mono">{formatoMXN(corte.ingresos_ventas)}</p>
        </div>
      </div>

      {/* Por metodo */}
      {corte.por_metodo && Object.keys(corte.por_metodo).length > 0 && (
        <div className="bg-slate-800/30 p-3 rounded-sm">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Por Metodo de Pago</p>
          {Object.entries(corte.por_metodo).map(([m, d]) => (
            <div key={m} className="flex justify-between text-sm py-0.5">
              <span className="text-slate-400 capitalize">{m} <span className="text-slate-600">({d.count})</span></span>
              <span className="text-slate-200 font-mono">{formatoMXN(d.total)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Por servicio */}
      {corte.por_servicio && Object.keys(corte.por_servicio).length > 0 && (
        <div className="bg-slate-800/30 p-3 rounded-sm">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Por Servicio</p>
          {Object.entries(corte.por_servicio).map(([s, d]) => (
            <div key={s} className="flex justify-between text-sm py-0.5">
              <span className="text-slate-400">{s} <span className="text-slate-600">({d.count})</span></span>
              <span className="text-cyan-400 font-mono">{formatoMXN(d.total)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Arqueo */}
      <div className={`p-3 rounded-sm border ${corte.diferencia_arqueo === 0 ? 'border-green-800/50 bg-green-500/5' : 'border-amber-800/50 bg-amber-500/5'}`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Arqueo de Efectivo</p>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Esperado:</span>
          <span className="text-slate-200 font-mono">{formatoMXN(corte.efectivo_esperado)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Contado:</span>
          <span className="text-slate-200 font-mono">{formatoMXN(corte.efectivo_real)}</span>
        </div>
        <div className={`flex justify-between text-sm font-bold mt-1 pt-1 border-t border-slate-800/50`}>
          <span className="text-slate-400">Diferencia:</span>
          <span className={`font-mono ${corte.diferencia_arqueo === 0 ? 'text-green-400' : 'text-amber-400'}`}>
            {corte.diferencia_arqueo === 0 ? 'Cuadra' : formatoMXN(corte.diferencia_arqueo)}
          </span>
        </div>
      </div>

      {/* Detalle cobros */}
      {corte.detalle_cobros?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Detalle de Cobros ({corte.detalle_cobros.length})</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {corte.detalle_cobros.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-slate-800/30 rounded-sm text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-slate-300 font-bold">{c.placa}</span>
                  <span className="text-slate-500">{c.servicio}</span>
                  <Badge className="text-[9px] bg-slate-700/50 text-slate-400 border-slate-600">{c.metodo}</Badge>
                  {c.descuento > 0 && <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-800">-{c.descuento}%</Badge>}
                </div>
                <span className="text-emerald-400 font-mono font-bold">{formatoMXN(c.precio_final)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notas */}
      {corte.notas && (
        <div className="bg-slate-800/30 p-3 rounded-sm">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Notas</p>
          <p className="text-sm text-slate-300">{corte.notas}</p>
        </div>
      )}
    </div>
  );
}
